import { useEffect, useMemo, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { CalendarDays, ChevronRight, MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { FeatureImage } from '@/components/FeatureImage';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { useLanguage } from '@/contexts/LanguageContext';

interface AgendaWidgetProps {
  municipalityId: string;
  municipalityName: string;
  fallbackImage?: string;
  fallbackClick?: () => void;
  onOpenAgenda: () => void;
}

interface AgendaEvent {
  id: string;
  title: string;
  date: string;
  endDate?: string;
  location?: string | null;
  imageUrl: string | null;
}

interface AgendaResponse {
  events?: AgendaEvent[];
}

const formatDate = (value: string, locale: string) => new Intl.DateTimeFormat(locale, {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/Madrid',
}).format(new Date(`${value}T12:00:00+02:00`));

const formatDateRange = (event: AgendaEvent, locale: string) => {
  if (!event.endDate || event.endDate === event.date) return formatDate(event.date, locale);
  return `${formatDate(event.date, locale)} · ${formatDate(event.endDate, locale)}`;
};

export const AgendaWidget = ({
  municipalityId,
  municipalityName,
  fallbackImage,
  fallbackClick,
  onOpenAgenda,
}: AgendaWidgetProps) => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      setLoading(true);
      try {
        // La fuente de eventos solo es fiable en español. La agenda se mantiene
        // en ese idioma aunque el resto del tótem cambie de idioma.
        const response = await api.events.list(municipalityId, 'es') as AgendaResponse;
        if (!cancelled) setEvents(response.events ?? []);
      } catch (error) {
        console.error('Error loading agenda widget:', error);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadEvents();
    const handleFocus = () => void loadEvents();
    window.addEventListener('focus', handleFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleFocus);
    };
  }, [municipalityId]);

  const visibleEvents = useMemo(
    () => events.filter((event) => event.imageUrl).slice(0, 5),
    [events],
  );

  if (loading) {
    return (
      <div className="bottom-image-container agenda-widget-state">
        <div className="feature-spinner" />
        <p>{t('agenda.updating')}</p>
      </div>
    );
  }

  if (visibleEvents.length === 0) {
    return (
      <FeatureImage
        municipalityId={municipalityId}
        alt={municipalityName}
        label={`${t('tourism.defaultEyebrow')} ${municipalityName}`}
        fallbackImage={fallbackImage}
        onClick={fallbackClick}
      />
    );
  }

  return (
    <section className="bottom-image-container agenda-widget" aria-label={t('agenda.eventsLabel')}>
      <Carousel
        opts={{ align: 'start', loop: visibleEvents.length > 1 }}
        plugins={[Autoplay({ delay: 5500, stopOnInteraction: false })]}
        className="w-full h-full"
      >
        <CarouselContent className="h-full">
          {visibleEvents.map((event) => (
            <CarouselItem key={event.id} className="h-full">
              <button type="button" className="agenda-widget-slide" onClick={onOpenAgenda}>
                <img src={event.imageUrl!} alt="" loading="eager" />
                <span className="agenda-widget-shade" />
                <span className="agenda-widget-content">
                  <span className="agenda-widget-kicker">
                    <CalendarDays /> {t('agenda.kicker')}
                  </span>
                  <strong>{event.title}</strong>
                  <span className="agenda-widget-meta">
                    <time dateTime={event.date}>{formatDateRange(event, 'es-ES')}</time>
                    {event.location && <span><MapPin /> {event.location}</span>}
                  </span>
                  <span className="agenda-widget-cta">{t('agenda.open')} <ChevronRight /></span>
                </span>
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
};
